/**
 * OverviewPage - Monthly Dashboard
 *
 * Shows financial overview matching the design mockup:
 * - Period selector (Week/Month/Year)
 * - Income vs Expense comparison bar
 * - Net Cash Flow
 * - vs. Last Month comparison
 * - Top Spending categories with progress bars (clickable for details)
 * - Spending Trend chart placeholder
 */

import { useAnalytics } from '@hooks/useAnalytics';
import { usePrivacyMode } from '@hooks/usePrivacyMode';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PrivacyToggle } from '@/components/shared';
import { CategoryDetailModal, CategoryWithStats } from '@/components/categories';
import { db, Category, Transaction, Account } from '@services/db';
import { transactionService } from '@services/transactions';
import { loadWatchGroups, buildCapitalMoveMatcher } from '@services/watchlist';
import {
  formatCurrency as formatCurrencyIntl,
  formatDate as formatDateIntl,
  formatMonthDay,
  formatMonthYear,
} from '@/utils/format';
import { getCategoryIcon } from '@/utils/categoryIcons';
import { useLanguage, categoryDisplayName } from '@/i18n';
import './OverviewPage.css';

type Period = 'week' | 'month' | '2months' | '3months' | '6months' | 'year';

// localStorage key for persisting period preference
const PERIOD_STORAGE_KEY = 'ninjacash-overview-period';

/**
 * Format currency for display.
 */
function formatCurrency(amount: number): string {
  // Central locale-aware formatter (utils/format.ts) - never inline Intl here
  return formatCurrencyIntl(amount, 'USD', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/**
 * Format period label based on period type.
 * - Week: "Dec 22 - Dec 28, 2025"
 * - Month: "December 2025"
 * - 3months: "Oct - Dec 2025"
 * - 6months: "Jul - Dec 2025"
 * - Year: "2025"
 */
function formatPeriodLabel(date: Date, period: Period): string {
  if (period === 'week') {
    // Show week range: "Dec 23 - Dec 29, 2025"
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay()); // Start of week (Sunday)
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const startStr = formatMonthDay(start);
    const endStr = formatDateIntl(end, { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} - ${endStr}`;
  } else if (period === 'year') {
    return date.getFullYear().toString();
  } else if (period === '2months') {
    // Show 2-month range: "Nov - Dec 2025"
    const endMonth = formatDateIntl(date, { month: 'short' });
    const startDate = new Date(date.getFullYear(), date.getMonth() - 1, 1);
    const startMonth = formatDateIntl(startDate, { month: 'short' });
    // Handle year boundary
    if (startDate.getFullYear() !== date.getFullYear()) {
      return `${startMonth} ${startDate.getFullYear()} - ${endMonth} ${date.getFullYear()}`;
    }
    return `${startMonth} - ${endMonth} ${date.getFullYear()}`;
  } else if (period === '3months') {
    // Show 3-month range: "Oct - Dec 2025"
    const endMonth = formatDateIntl(date, { month: 'short' });
    const startDate = new Date(date.getFullYear(), date.getMonth() - 2, 1);
    const startMonth = formatDateIntl(startDate, { month: 'short' });
    // Handle year boundary
    if (startDate.getFullYear() !== date.getFullYear()) {
      return `${startMonth} ${startDate.getFullYear()} - ${endMonth} ${date.getFullYear()}`;
    }
    return `${startMonth} - ${endMonth} ${date.getFullYear()}`;
  } else if (period === '6months') {
    // Show 6-month range: "Jul - Dec 2025"
    const endMonth = formatDateIntl(date, { month: 'short' });
    const startDate = new Date(date.getFullYear(), date.getMonth() - 5, 1);
    const startMonth = formatDateIntl(startDate, { month: 'short' });
    // Handle year boundary (e.g., Oct 2024 - Mar 2025)
    if (startDate.getFullYear() !== date.getFullYear()) {
      return `${startMonth} ${startDate.getFullYear()} - ${endMonth} ${date.getFullYear()}`;
    }
    return `${startMonth} - ${endMonth} ${date.getFullYear()}`;
  } else {
    // Month
    return formatMonthYear(date);
  }
}

/**
 * Get date range for a period.
 */
function getDateRange(baseDate: Date, period: Period): { start: Date; end: Date } {
  if (period === 'week') {
    const start = new Date(baseDate);
    start.setDate(start.getDate() - start.getDay()); // Start of week (Sunday)
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  } else if (period === 'year') {
    const start = new Date(baseDate.getFullYear(), 0, 1);
    const end = new Date(baseDate.getFullYear(), 11, 31, 23, 59, 59, 999);
    return { start, end };
  } else if (period === '2months') {
    // Last 2 months including current
    const start = new Date(baseDate.getFullYear(), baseDate.getMonth() - 1, 1);
    const end = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start, end };
  } else if (period === '3months') {
    // Last 3 months including current
    const start = new Date(baseDate.getFullYear(), baseDate.getMonth() - 2, 1);
    const end = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start, end };
  } else if (period === '6months') {
    // Last 6 months including current
    const start = new Date(baseDate.getFullYear(), baseDate.getMonth() - 5, 1);
    const end = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start, end };
  } else {
    // Month
    const start = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
    const end = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0, 23, 59, 59, 999);
    return { start, end };
  }
}

/**
 * Navigate to previous/next period.
 */
function navigatePeriod(baseDate: Date, period: Period, direction: -1 | 1): Date {
  const newDate = new Date(baseDate);
  if (period === 'week') {
    newDate.setDate(newDate.getDate() + direction * 7);
  } else if (period === 'year') {
    newDate.setFullYear(newDate.getFullYear() + direction);
  } else if (period === '2months') {
    // Navigate by 2 months at a time
    newDate.setMonth(newDate.getMonth() + direction * 2);
  } else if (period === '3months') {
    // Navigate by 3 months at a time
    newDate.setMonth(newDate.getMonth() + direction * 3);
  } else if (period === '6months') {
    // Navigate by 6 months at a time
    newDate.setMonth(newDate.getMonth() + direction * 6);
  } else {
    // Month
    newDate.setMonth(newDate.getMonth() + direction);
  }
  return newDate;
}

// Category colors for spending bars
const CATEGORY_COLORS: Record<string, string> = {
  'Housing': '#9d7aff',
  'Food': '#ff6b6b',
  'Food & Dining': '#ff6b6b',
  'Shopping': '#00d68f',
  'Transport': '#4ecdc4',
  'Transportation': '#4ecdc4',
  'Insurance': '#888888',
  'Utilities': '#0066ff',
  'Entertainment': '#f59e0b',
  'Bills': '#9d7aff',
  'Health': '#00d68f',
  'Education': '#f7dc6f',
  'Other': '#888888',
};

export default function OverviewPage() {
  const { t } = useLanguage();
  const { isPrivacyMode } = usePrivacyMode();
  // Load saved period from localStorage, default to '3months'
  const [period, setPeriod] = useState<Period>(() => {
    const saved = localStorage.getItem(PERIOD_STORAGE_KEY);
    if (saved && ['week', 'month', '2months', '3months', '6months', 'year'].includes(saved)) {
      return saved as Period;
    }
    return '3months';
  });
  const [baseDate, setBaseDate] = useState<Date>(new Date());

  // State for category detail modal
  const [selectedCategory, setSelectedCategory] = useState<CategoryWithStats | null>(null);
  const [modalMonth, setModalMonth] = useState<Date>(() => new Date());
  const [categories, setCategories] = useState<Category[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);

  // Calculate date range based on period and baseDate
  const dateRange = useMemo(() => getDateRange(baseDate, period), [baseDate, period]);

  // Capital-move exclusion (same shared detector as Analytics/Crypto):
  // money moved to external wallets is not consumption, so Overview totals,
  // top spending, and trends all exclude it - consistent across pages.
  // Loaded once per mount; Overview has no group editor.
  const capitalMoveMatcher = useMemo(
    () => buildCapitalMoveMatcher(loadWatchGroups()) || undefined,
    []
  );

  const { data: analytics, loading: analyticsLoading, setDateRange: setAnalyticsDateRange } =
    useAnalytics(6, { capitalMoveMatcher });

  const isLoading = analyticsLoading;

  // Real previous-period totals for the "vs previous" cards (the old cards
  // showed hardcoded mock +12%/+8% values). Same exclusions as the current
  // period: adjustments + capital moves.
  const [prevTotals, setPrevTotals] = useState<{ income: number; expense: number } | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const prevRange = getDateRange(navigatePeriod(baseDate, period, -1), period);
        const txns = await transactionService.getFiltered({
          startDate: prevRange.start,
          endDate: prevRange.end,
          limit: 100000,
        });
        let income = 0;
        let expense = 0;
        for (const txn of txns) {
          const desc = (txn.description || '').toLowerCase();
          if (desc.includes('[adjustment]') || desc.startsWith('adjustment')) continue;
          if (capitalMoveMatcher && capitalMoveMatcher(txn.description || '')) continue;
          if (txn.type === 'income') income += txn.amount;
          else if (txn.type === 'expense') expense += txn.amount;
        }
        if (!cancelled) setPrevTotals({ income, expense });
      } catch (err) {
        console.error('[OverviewPage] Previous-period totals failed:', err);
        if (!cancelled) setPrevTotals(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [baseDate, period, capitalMoveMatcher]);

  // BUG FIX: Sync analytics date range on mount and whenever dateRange changes
  // Without this, useAnalytics() defaults to 6 months while useSummary uses the selected period
  useEffect(() => {
    setAnalyticsDateRange({ start: dateRange.start, end: dateRange.end });
  }, [dateRange.start, dateRange.end, setAnalyticsDateRange]);

  // Navigation handlers
  const handlePrevious = useCallback(() => {
    const newDate = navigatePeriod(baseDate, period, -1);
    setBaseDate(newDate);
    // Sync analytics date range
    const newRange = getDateRange(newDate, period);
    setAnalyticsDateRange(newRange);
  }, [period, baseDate, setAnalyticsDateRange]);

  const handleNext = useCallback(() => {
    const newDate = navigatePeriod(baseDate, period, 1);
    setBaseDate(newDate);
    // Sync analytics date range
    const newRange = getDateRange(newDate, period);
    setAnalyticsDateRange(newRange);
  }, [period, baseDate, setAnalyticsDateRange]);

  // Reset to current period when changing period type
  const handlePeriodChange = useCallback((newPeriod: Period) => {
    setPeriod(newPeriod);
    // Persist preference to localStorage
    localStorage.setItem(PERIOD_STORAGE_KEY, newPeriod);
    const today = new Date();
    setBaseDate(today);
    // Sync analytics date range
    const newRange = getDateRange(today, newPeriod);
    setAnalyticsDateRange(newRange);
  }, [setAnalyticsDateRange]);

  // Load categories, transactions, accounts for modal
  useEffect(() => {
    const loadData = async () => {
      try {
        const [cats, txns, accs] = await Promise.all([
          db.categories.toArray(),
          db.transactions.toArray(),
          db.accounts.toArray(),
        ]);
        setCategories(cats);
        setTransactions(txns);
        setAccounts(accs);
      } catch (err) {
        console.error('[OverviewPage] Failed to load data for modal:', err);
      }
    };
    loadData();
  }, []);

  // Handle clicking a category to open detail modal
  const handleCategoryClick = useCallback((catData: {
    categoryId: string;
    categoryName: string;
    categoryColor: string;
    amount: number;
    categoryIds?: string[];
    isUncategorized?: boolean;
  }) => {
    // Find the full category record
    const fullCategory = categories.find(c => c.id === catData.categoryId);
    const knownCategoryIds = new Set(categories.map(c => c.id));
    const categoryIds = new Set(catData.categoryIds || [catData.categoryId]);
    const matchingTransactions = transactions.filter(t => {
      if (catData.isUncategorized) {
        return t.type === 'expense' && (!t.categoryId || !knownCategoryIds.has(t.categoryId));
      }
      return t.categoryId ? categoryIds.has(t.categoryId) : false;
    });

    // Build CategoryWithStats - create virtual category for Unknown items
    const categoryWithStats: CategoryWithStats = fullCategory
      ? {
          ...fullCategory,
          amount: catData.amount,
          transactionCount: matchingTransactions.length,
        }
      : {
          // Virtual category for uncategorized or deleted/missing category records.
          id: catData.categoryId,
          name: catData.categoryName,
          type: 'expense',
          color: catData.categoryColor || '#888888',
          isActive: true,
          amount: catData.amount,
          transactionCount: matchingTransactions.length,
        };

    // Set modal month to end of current date range
    setModalMonth(new Date(dateRange.end));
    setSelectedCategory(categoryWithStats);
  }, [categories, transactions, dateRange.end]);

  // Handle modal month navigation
  const handleModalMonthChange = useCallback((direction: 'prev' | 'next') => {
    setModalMonth(prev => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + (direction === 'next' ? 1 : -1));
      return d;
    });
  }, []);

  // Comparison bar uses the analytics totals (adjustments + capital moves
  // excluded - identical numbers to the Analytics page)
  const periodIncome = analytics.totalIncome;
  const periodExpense = analytics.totalExpense;
  const periodNet = periodIncome - periodExpense;
  const total = periodIncome + periodExpense;
  const incomePercent = total > 0 ? (periodIncome / total) * 100 : 50;
  const expensePercent = total > 0 ? (periodExpense / total) * 100 : 50;

  // Real vs-previous-period deltas (replaces the old hardcoded mock values)
  const vsCards = useMemo(() => {
    if (!prevTotals) return null;
    const prevNet = prevTotals.income - prevTotals.expense;
    const build = (current: number, previous: number) => {
      const delta = current - previous;
      const percent = previous > 0 ? (delta / previous) * 100 : null;
      return { delta, percent };
    };
    return {
      income: build(periodIncome, prevTotals.income),
      expense: build(periodExpense, prevTotals.expense),
      net: build(periodNet, prevNet),
    };
  }, [prevTotals, periodIncome, periodExpense, periodNet]);

  const hasBaseline = Boolean(
    prevTotals && (prevTotals.income > 0 || prevTotals.expense > 0)
  );

  // Get top spending categories (already filtered to expenses and sorted in useAnalytics)
  const topSpending = analytics.categorySpending.slice(0, 12);

  const maxSpending = topSpending[0]?.amount || 1;

  // Real monthly trend bars (replaces the old Math.random() placeholder);
  // only meaningful with 2+ months in range
  const trendMonths = analytics.monthlyTrends.slice(-6);
  const showTrend = trendMonths.filter((m) => m.hasData).length >= 2;
  const maxTrend = Math.max(...trendMonths.map((m) => Math.max(m.income, m.expense)), 1);

  return (
    <div className="overview-page">
      {/* Header */}
      <header className="header">
        <span className="header-title">{t('overview.title')}</span>
        <div className="header-actions">
          <PrivacyToggle />
          <button className="btn-icon-sm btn-ghost">📅</button>
        </div>
      </header>

      {/* Period Selector */}
      <div className="period-selector">
        <button
          className={`period-btn ${period === 'week' ? 'active' : ''}`}
          onClick={() => handlePeriodChange('week')}
        >
          {t('overview.period.week')}
        </button>
        <button
          className={`period-btn ${period === 'month' ? 'active' : ''}`}
          onClick={() => handlePeriodChange('month')}
        >
          {t('overview.period.month')}
        </button>
        <button
          className={`period-btn ${period === '2months' ? 'active' : ''}`}
          onClick={() => handlePeriodChange('2months')}
        >
          {t('overview.period.m2')}
        </button>
        <button
          className={`period-btn ${period === '3months' ? 'active' : ''}`}
          onClick={() => handlePeriodChange('3months')}
        >
          {t('overview.period.m3')}
        </button>
        <button
          className={`period-btn ${period === '6months' ? 'active' : ''}`}
          onClick={() => handlePeriodChange('6months')}
        >
          {t('overview.period.m6')}
        </button>
        <button
          className={`period-btn ${period === 'year' ? 'active' : ''}`}
          onClick={() => handlePeriodChange('year')}
        >
          {t('overview.period.year')}
        </button>
      </div>

      {/* Time Navigation */}
      <div className="time-filter">
        <div className="time-nav">
          <button className="time-btn" onClick={handlePrevious}>←</button>
          <span className="time-label">{formatPeriodLabel(baseDate, period)}</span>
          <button className="time-btn" onClick={handleNext}>→</button>
        </div>
      </div>

      {/* Income vs Expense Comparison */}
      <div className="comparison-card">
        <div className="comparison-bar">
          <div
            className="comparison-segment income-segment"
            style={{ width: `${incomePercent}%` }}
          />
          <div
            className="comparison-segment expense-segment"
            style={{ width: `${expensePercent}%` }}
          />
        </div>
        <div className="comparison-labels">
          <div className="comparison-item">
            <span className="comparison-dot income-dot" />
            <span className="comparison-label">{t('common.income')}</span>
            <span className="comparison-value amount--income">
              {isLoading ? '--' : (
                isPrivacyMode ? (
                  <span className="private-amount masked">••••</span>
                ) : (
                  formatCurrency(periodIncome)
                )
              )}
            </span>
          </div>
          <div className="comparison-item">
            <span className="comparison-dot expense-dot" />
            <span className="comparison-label">{t('common.expenses')}</span>
            <span className="comparison-value amount--expense">
              {isLoading ? '--' : (
                isPrivacyMode ? (
                  <span className="private-amount masked">••••</span>
                ) : (
                  formatCurrency(periodExpense)
                )
              )}
            </span>
          </div>
        </div>
        <div className="net-flow">
          <span className="net-label">{t('overview.net_cash_flow')}</span>
          <span className={`net-value ${periodNet >= 0 ? 'amount--income' : 'amount--expense'}`}>
            {isLoading ? '--' : (
              isPrivacyMode ? (
                <span className="private-amount masked">••••</span>
              ) : (
                `${periodNet >= 0 ? '+' : ''}${formatCurrency(periodNet)}`
              )
            )}
          </span>
        </div>
        {!isLoading && analytics.capitalMoves.count > 0 && (
          <div className="capital-note">
            {isPrivacyMode
              ? t('analytics.summary.capital_excluded_hidden')
              : t('analytics.summary.capital_excluded', {
                  amount: formatCurrency(analytics.capitalMoves.expense),
                })}
          </div>
        )}
      </div>

      {/* vs Previous Period - REAL deltas (was hardcoded mock data) */}
      {!isLoading && vsCards && hasBaseline && (
        <div className="comparison-row">
          {([
            {
              key: 'income',
              stats: vsCards.income,
              goodWhenUp: true,
              upKey: 'analytics.mom.income_up',
              downKey: 'analytics.mom.income_down',
              upHiddenKey: 'analytics.mom.income_up_hidden',
              downHiddenKey: 'analytics.mom.income_down_hidden',
              maskAmount: true,
            },
            {
              key: 'expense',
              stats: vsCards.expense,
              goodWhenUp: false,
              upKey: 'analytics.mom.expenses_up',
              downKey: 'analytics.mom.expenses_down',
              upHiddenKey: 'analytics.mom.expenses_up_hidden',
              downHiddenKey: 'analytics.mom.expenses_down_hidden',
              maskAmount: false,
            },
            {
              key: 'net',
              stats: vsCards.net,
              goodWhenUp: true,
              upKey: 'analytics.mom.net_up',
              downKey: 'analytics.mom.net_down',
              upHiddenKey: 'analytics.mom.net_up_hidden',
              downHiddenKey: 'analytics.mom.net_down_hidden',
              maskAmount: true,
            },
          ] as const).map(({ key, stats, goodWhenUp, upKey, downKey, upHiddenKey, downHiddenKey, maskAmount }) => {
            const isUp = stats.delta >= 0;
            const good = isUp === goodWhenUp;
            const percentLabel = stats.percent !== null
              ? `${isUp ? '+' : '-'}${Math.abs(stats.percent).toFixed(0)}%`
              : `${isUp ? '+' : '-'}${formatCurrency(Math.abs(stats.delta))}`;
            const hideAmount = isPrivacyMode && maskAmount;
            const detail = hideAmount
              ? t(isUp ? upHiddenKey : downHiddenKey)
              : t(isUp ? upKey : downKey, {
                  amount: formatCurrency(Math.abs(stats.delta)),
                  percent: stats.percent !== null ? `${Math.abs(stats.percent).toFixed(0)}%` : '--',
                });
            return (
              <div className="vs-card" key={key}>
                <div className="vs-label">{t('overview.vs_previous')}</div>
                <div className={`vs-change ${good ? 'positive' : 'negative'}`}>
                  {hideAmount && stats.percent === null ? '•••' : percentLabel}
                </div>
                <div className="vs-detail">{detail}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Top Spending Categories */}
      <div className="section-head">
        <span className="section-title">{t('overview.top_spending')}</span>
        <Link to="/analytics" className="section-action">{t('overview.see_all')}</Link>
      </div>

      <div className="list-card">
        {isLoading ? (
          <div className="section-loading">{t('common.loading')}</div>
        ) : topSpending.length === 0 ? (
          <div className="section-empty">{t('overview.no_spending')}</div>
        ) : (
          topSpending.map((cat: {
            categoryId: string;
            categoryName: string;
            categoryColor: string;
            amount: number;
            percentage: number;
            categoryIds?: string[];
            isUncategorized?: boolean;
          }, index: number) => {
            const percent = Math.round(cat.percentage);
            const barWidth = Math.round((cat.amount / maxSpending) * 100);
            const color = CATEGORY_COLORS[cat.categoryName] || cat.categoryColor || '#888888';
            // Real category icon via the shared resolver (exact + keyword)
            const catRecord = categories.find((c) => c.id === cat.categoryId);
            const icon = getCategoryIcon(catRecord?.name ?? cat.categoryName, catRecord?.icon);

            return (
              <button
                key={cat.categoryId}
                type="button"
                className="overview-row overview-row--clickable"
                onClick={() => handleCategoryClick(cat)}
              >
                <div className="overview-rank">{index + 1}</div>
                <div
                  className="overview-icon"
                  style={{ background: `${color}20`, color }}
                >
                  {icon}
                </div>
                <div className="overview-info">
                  <div className="overview-name">{categoryDisplayName(cat.categoryName)}</div>
                  <div className="overview-bar">
                    <div
                      className="bar-fill"
                      style={{ width: `${barWidth}%`, background: color }}
                    />
                  </div>
                </div>
                <div className="overview-amount amount--expense">
                  {formatCurrency(cat.amount)}
                </div>
                <div className="overview-percent">{percent}%</div>
              </button>
            );
          })
        )}
      </div>

      {/* Spending Trend Chart - real monthly data (was a Math.random()
          placeholder with hardcoded month labels). Hidden when the selected
          period spans fewer than 2 months of data. */}
      {showTrend && (
        <>
          <div className="section-head">
            <span className="section-title">{t('overview.spending_trend')}</span>
          </div>

          <div className="chart-card">
            <div className="chart-placeholder">
              <div className="chart-bars">
                {trendMonths.map((m) => (
                  <div key={m.monthKey} className="chart-bar-group" title={m.fullLabel}>
                    <div
                      className="chart-bar chart-bar--income"
                      style={{ height: `${Math.max(3, (m.income / maxTrend) * 100)}%` }}
                    />
                    <div
                      className="chart-bar chart-bar--expense"
                      style={{ height: `${Math.max(3, (m.expense / maxTrend) * 100)}%` }}
                    />
                    <span className="chart-bar-label">{m.month}</span>
                  </div>
                ))}
              </div>
              <div className="chart-legend">
                <span className="legend-item">
                  <span className="legend-dot" style={{ background: 'var(--income)' }} />
                  {t('common.income')}
                </span>
                <span className="legend-item">
                  <span className="legend-dot" style={{ background: 'var(--expense)' }} />
                  {t('common.expense')}
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Quick Links */}
      <div className="quick-links">
        <Link to="/analytics" className="quick-link">
          <span className="quick-link-icon">📈</span>
          <span className="quick-link-text">{t('nav.analytics')}</span>
          <span className="quick-link-arrow">→</span>
        </Link>
        <Link to="/categories" className="quick-link">
          <span className="quick-link-icon">🏷️</span>
          <span className="quick-link-text">{t('overview.all_categories')}</span>
          <span className="quick-link-arrow">→</span>
        </Link>
      </div>

      {/* Category Detail Modal */}
      <CategoryDetailModal
        category={selectedCategory}
        transactions={transactions}
        accounts={accounts}
        currentMonth={modalMonth}
        onClose={() => setSelectedCategory(null)}
        onMonthChange={handleModalMonthChange}
        showRename={false}
        dateRange={dateRange}
      />
    </div>
  );
}
